/**
 * Agency portal — Activity feed (SYR-85).
 *
 * Cross-client event stream with client filter chips.
 */

import React, { useMemo, useState } from 'react';
import { Empty, Spin } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAuth } from '../../hooks/useAuth';
import {
    useGetAgencyDashboardActivityQuery,
    useGetAgencyClientsQuery,
} from '../../services/nestjs-base-api';
import { Card } from '../../components/MarketerView/atoms';
import { THEME } from '../../components/MarketerView/config/theme';

const EVENT_COLORS: Record<string, string> = {
    RESPONSE_SUBMITTED: '#16a34a',
    SURVEY_CREATED: '#3b82f6',
    SURVEY_UPDATED: '#3b82f6',
    USER_LOGIN: '#94a3b8',
    ORGANIZATION_UPDATED: '#8b5cf6',
};

function timeAgo(iso: string): string {
    const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
}

function formatEventType(eventType: string): string {
    return eventType
        .toLowerCase()
        .split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
}

const PAGE_SIZE = 50;

export const AgencyActivityPage: React.FunctionComponent = () => {
    const intl = useIntl();
    const { user } = useAuth();
    const jwtToken = user?.jwtToken || '';
    const [clientFilter, setClientFilter] = useState<number | null>(null);
    const [limit, setLimit] = useState(PAGE_SIZE);

    const { data: feed, isLoading } = useGetAgencyDashboardActivityQuery({
        jwtToken,
        limit,
        offset: 0,
    });
    const { data: clients } = useGetAgencyClientsQuery({ jwtToken });

    const items = useMemo(() => {
        const all = feed?.items || [];
        return clientFilter
            ? all.filter((i) => i.clientOrganisationId === clientFilter)
            : all;
    }, [feed, clientFilter]);

    const chipStyle = (active: boolean): React.CSSProperties => ({
        padding: '6px 14px',
        borderRadius: 999,
        border: `1px solid ${active ? THEME.primary : THEME.border}`,
        background: active ? THEME.primarySoft : THEME.bgChip,
        color: active ? THEME.primaryDark : THEME.textBase,
        fontWeight: active ? 600 : 500,
        fontSize: 13,
        cursor: 'pointer',
    });

    return (
        <div>
            <div style={{ marginBottom: 24 }}>
                <h1
                    style={{
                        fontSize: 30,
                        fontWeight: 700,
                        letterSpacing: '-0.02em',
                        margin: 0,
                        lineHeight: 1.12,
                    }}
                >
                    <FormattedMessage id="agency.activity.title" defaultMessage="Activity" />
                </h1>
                <p style={{ color: THEME.textSecondary, fontSize: 15.5, margin: '6px 0 0' }}>
                    <FormattedMessage
                        id="agency.activity.subtitle"
                        defaultMessage="Everything happening across your client portfolio."
                    />
                </p>
            </div>

            {/* Client filter chips — hidden when only one client */}
            {(clients?.length || 0) > 1 && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
                    <button
                        type="button"
                        style={chipStyle(clientFilter === null)}
                        onClick={() => setClientFilter(null)}
                    >
                        {intl.formatMessage({
                            id: 'agency.activity.allClients',
                            defaultMessage: 'All clients',
                        })}
                    </button>
                    {(clients || []).map((c) => (
                        <button
                            key={c.clientOrganisationId}
                            type="button"
                            style={chipStyle(clientFilter === c.clientOrganisationId)}
                            onClick={() => setClientFilter(c.clientOrganisationId)}
                        >
                            {c.clientOrganisation?.name || c.clientOrganisationId}
                        </button>
                    ))}
                </div>
            )}

            <Card pad={0}>
                {isLoading ? (
                    <div style={{ padding: 48, textAlign: 'center' }}>
                        <Spin size="large" />
                    </div>
                ) : items.length === 0 ? (
                    <div style={{ padding: 48 }}>
                        <Empty
                            description={intl.formatMessage({
                                id: 'agency.activity.empty',
                                defaultMessage: 'No activity yet',
                            })}
                        />
                    </div>
                ) : (
                    <div>
                        {items.map((item, idx) => (
                            <div
                                key={item.id}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 14,
                                    padding: '14px 24px',
                                    borderBottom:
                                        idx < items.length - 1
                                            ? `1px solid ${THEME.borderSubtle}`
                                            : 'none',
                                }}
                            >
                                <span
                                    style={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        background: EVENT_COLORS[item.eventType] || THEME.textMuted,
                                        flexShrink: 0,
                                    }}
                                />
                                <span
                                    style={{
                                        fontSize: 13,
                                        fontWeight: 600,
                                        color: THEME.textBase,
                                        minWidth: 160,
                                    }}
                                >
                                    {formatEventType(item.eventType)}
                                </span>
                                <span
                                    style={{
                                        fontSize: 13.5,
                                        color: THEME.textSecondary,
                                        flex: 1,
                                        minWidth: 0,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {item.clientOrganisationName}
                                </span>
                                <span
                                    style={{
                                        fontSize: 12.5,
                                        color: THEME.textMuted,
                                        flexShrink: 0,
                                    }}
                                >
                                    {timeAgo(item.createdAt)}
                                </span>
                            </div>
                        ))}
                        {(feed?.total || 0) > limit && clientFilter === null && (
                            <div style={{ padding: 16, textAlign: 'center' }}>
                                <button
                                    type="button"
                                    onClick={() => setLimit((l) => l + PAGE_SIZE)}
                                    style={{
                                        border: `1px solid ${THEME.border}`,
                                        background: '#fff',
                                        color: THEME.textBase,
                                        fontSize: 13.5,
                                        fontWeight: 600,
                                        padding: '8px 18px',
                                        borderRadius: 8,
                                        cursor: 'pointer',
                                    }}
                                >
                                    {intl.formatMessage({
                                        id: 'agency.activity.loadMore',
                                        defaultMessage: 'Load more',
                                    })}
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </Card>
        </div>
    );
};
